import { DashboardView } from '../../../types/accounting';

export type NavGroupId = 'enter' | 'bank' | 'reports' | 'manage';

export interface NavItem {
  label: string;
  icon: string;
  view?: DashboardView;        // opens this view
  action?: 'invite-users';     // or runs an action instead of opening a view
  track?: string;              // analytics event sent when clicked
}

export interface NavGroup {
  id: NavGroupId;
  label: string;
  items: NavItem[];
}

/** The two-step menu: a group bar (Enter, Bank, Reports, Manage), then the open group's items */
export const buildNavGroups = (canSeeReports: boolean, canInviteUsers: boolean): NavGroup[] => [
  {
    id: 'enter',
    label: 'Enter',
    items: [
      { view: 'add-expense', label: 'Add Expense', icon: '💰' },
      { view: 'simple-entry', label: 'Simple Entry', icon: '✏️' },
      { view: 'new-entry', label: 'Journal Entry', icon: '📝' },
      { view: 'recurring', label: 'Recurring', icon: '🔄' },
      { view: 'invoices', label: 'Invoices', icon: '📄' }
    ]
  },
  {
    id: 'bank',
    label: 'Bank',
    items: [
      { view: 'bank-import', label: 'Bank Import', icon: '🏦' },
      { view: 'reconciliation', label: 'Reconcile', icon: '✅' }
    ]
  },
  {
    id: 'reports',
    label: 'Reports',
    items: [
      { view: 'trial-balance', label: 'Trial Balance', icon: '📊', track: 'accounting_trial_balance_view' },
      ...(canSeeReports ? ([
        { view: 'balance-sheet', label: 'Balance Sheet', icon: '📄', track: 'accounting_balance_sheet_view' },
        { view: 'income-statement', label: 'P&L Statement', icon: '📈', track: 'accounting_income_statement_view' },
        { view: 'cash-flow', label: 'Cash Flow', icon: '💵', track: 'accounting_cash_flow_view' },
        { view: 'budget', label: 'Budget', icon: '📊', track: 'accounting_budget_view' },
        { view: 'general-ledger', label: 'General Ledger', icon: '📒', track: 'accounting_general_ledger_view' },
        { view: 'aging', label: 'AR Aging', icon: '📅', track: 'accounting_aging_report_view' },
        { view: 'sales-tax', label: 'Sales Tax', icon: '🧾', track: 'accounting_sales_tax_view' }
      ] as NavItem[]) : [])
    ]
  },
  {
    id: 'manage',
    label: 'Manage',
    items: [
      { view: 'accounts', label: 'Accounts', icon: '📁' },
      { view: 'periods', label: 'Periods', icon: '📅' },
      { view: 'journal-entries', label: 'All Entries', icon: '📋' },
      { view: 'contacts', label: 'Contacts', icon: '👤' },
      ...(canInviteUsers ? ([{ action: 'invite-users', label: 'Invite Users', icon: '👥' }] as NavItem[]) : []),
      { view: 'settings', label: 'Settings', icon: '⚙️' }
    ]
  }
];

/** Which group a view lives in; the dashboard is the home button, not a group */
export const groupOfView = (groups: NavGroup[], view: DashboardView): NavGroupId | null =>
  groups.find(group => group.items.some(item => item.view === view))?.id ?? null;
